/**
 * WordPickScreen: M4 batch 6a.
 *
 * The drawer's pick UI: 3 scribble cards, one per difficulty tier (Easy /
 * Medium / Hard). Click (or press 1 / 2 / 3) commits a PICK_WORD frame.
 * After commit the cards disable so further clicks are no-ops, the
 * server's PHASE_END (wordPick → drawing) will route us away.
 *
 * Sibling `WaitingForDrawer` renders for everyone else during wordPick
 * phase ("Bob is picking a word…").
 *
 * Tier badge testids: `tier-badge-easy` / `tier-badge-medium` /
 * `tier-badge-hard`. Card testids: `word-card-0` / `word-card-1` /
 * `word-card-2` (slate index) so E2E can click without depending on
 * a particular word.
 *
 * Aesthetics piggy-back on the existing scribble-btn + lobby paper
 * primitives: see `client/index.html` for the CSS rules.
 */

import { useCallback, useEffect, useState } from "preact/hooks";
import type { SerializedPlayer, WordChoice } from "@drawlol/shared";
import { DIFFICULTY_MULTIPLIER } from "@drawlol/shared";
import type { ClientControlFrame, GameStateView } from "../ws.js";
import { ScribbleButton } from "../components/ScribbleButton.js";
import { DrawingCanvas } from "../canvas/DrawingCanvas.js";
import { PlayersPanel } from "../components/PlayersPanel.js";
import { GuessChatFeed } from "../components/GuessChatFeed.js";
import { DrawingPhaseTimer } from "../components/DrawingPhaseTimer.js";
import { buildTurnScores } from "../game/turnScores.js";
import { useDeadlineCountdown } from "../hooks/useDeadlineCountdown.js";
import { PAGE_TEAR_IN_CLASS, PageTearLine } from "../anim/page-tear.js";

const KEY_FOR_INDEX = ["1", "2", "3"] as const;

// ---------------------------------------------------------------------------
// WordPickScreen: drawer's pick UI
// ---------------------------------------------------------------------------

type WordPickScreenProps = {
  /**
   * The 3-word slate offered to the drawer at PHASE_END(→wordPick) entry.
   * The server's snapshotRedaction strips this for non-drawer recipients,
   * if it's undefined here, treat it as a race-condition loading state.
   */
  wordChoices: ReadonlyArray<WordChoice> | undefined;
  /** Wall-clock ms remaining in the wordPick phase (countdown). */
  msRemaining: number;
  /** WS sender. Provided by RoomShell so the click commits over the wire. */
  sendFrame: (frame: ClientControlFrame) => boolean;
  /**
   * Total wordPick seconds (room setting), drives the urgency ramp. The
   * audience side always had the full DrawingPhaseTimer; the DRAWER, whom
   * auto-advance actually bites: had bare micro-text (audit 2026-06-11).
   */
  phaseTotalSeconds?: number;
};

export function WordPickScreen({
  wordChoices,
  msRemaining,
  sendFrame,
  phaseTotalSeconds,
}: WordPickScreenProps) {
  const [picked, setPicked] = useState<number | null>(null);
  // Codex M4 review P2-11: countdown was frozen at the first
  // ROOM_STATE_SYNC's `msRemaining` for the entire wordPick phase
  // (server only pushes snapshots at phase boundaries). Local
  // 250ms-tick derives the displayed time from a wall-clock anchor.
  const secondsLeft = useDeadlineCountdown(msRemaining);

  const handlePick = useCallback(
    (index: number) => {
      if (picked !== null) return; // already picked: server will route us away
      if (!wordChoices) return; // race condition: no slate yet
      const choice = wordChoices[index];
      if (!choice) return;
      setPicked(index);
      sendFrame({ kind: "PICK_WORD", word: choice.word, tier: choice.tier });
    },
    [picked, wordChoices, sendFrame],
  );

  // Keyboard shortcut: 1 / 2 / 3 picks the matching card.
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (picked !== null) return;
      const idx = KEY_FOR_INDEX.indexOf(ev.key as (typeof KEY_FOR_INDEX)[number]);
      if (idx === -1) return;
      handlePick(idx);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [picked, handlePick]);

  // Loading / race state: slate hasn't arrived yet.
  if (!wordChoices || wordChoices.length === 0) {
    return (
      <div
        class={`word-pick-screen ${PAGE_TEAR_IN_CLASS}`}
        data-testid="word-pick-screen"
        role="status"
        aria-live="polite"
      >
        <PageTearLine />
        <p class="word-pick-loading">Loading your words…</p>
      </div>
    );
  }

  return (
    <div
      class={`word-pick-screen ${PAGE_TEAR_IN_CLASS}`}
      data-testid="word-pick-screen"
      role="region"
      aria-label="Pick your word"
    >
      <PageTearLine />
      {/* V4.2.T12: small muted eyebrow above the big-bold heading, consistent
          with the T8 "DRAW THIS" eyebrow → big word treatment. */}
      <p class="word-pick-eyebrow" data-testid="word-pick-eyebrow">
        Your turn
      </p>
      <h1 class="word-pick-heading">Pick a word to draw</h1>
      <div class="word-pick-timer-row">
        <DrawingPhaseTimer secondsLeft={secondsLeft} totalSeconds={phaseTotalSeconds} />
      </div>
      <div class="word-pick-cards" role="group" aria-label="Word choices">
        {wordChoices.map((choice, i) => (
          <ScribbleButton
            key={`${choice.tier}-${choice.word}`}
            variant={i === picked ? "primary" : "secondary"}
            disabled={picked !== null && picked !== i}
            onClick={() => handlePick(i)}
            class="word-pick-card"
            aria-label={`Pick "${choice.word}" (${choice.tier}, ×${DIFFICULTY_MULTIPLIER[choice.tier]} points)`}
          >
            <span class="word-pick-word" data-testid={`word-card-${i}`}>
              {choice.word}
            </span>
            <span
              class={`word-pick-tier word-pick-tier--${choice.tier}`}
              data-testid={`tier-badge-${choice.tier}`}
            >
              {/* Stakes at the only moment the multiplier is a DECISION
                  (audit 2026-06-11: scoring was explained nowhere). */}
              {choice.tier} ×{DIFFICULTY_MULTIPLIER[choice.tier]}
            </span>
          </ScribbleButton>
        ))}
      </div>
      {picked !== null && (
        <p class="word-pick-confirm" role="status">
          Picked! Get ready…
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// WaitingForDrawer: what everyone else sees during wordPick (V4.2.T12, note 5e)
// ---------------------------------------------------------------------------

type WaitingForDrawerProps = {
  /** Display name of the drawer who's picking, shown in the banner. */
  drawerName: string;
  /** The active drawer's id: gives the left panel its drawer accent. */
  drawerId: string;
  /** Local player's id: "me" highlight in the left panel. */
  playerId: string | null;
  /** Cached game-state projection from RoomShell (no currentTurn during pick). */
  game: GameStateView;
  /** Player roster: drives the left <PlayersPanel> avatars + scores. */
  players: ReadonlyArray<SerializedPlayer>;
  /** Room code: drives the persistent in-game invite chip in the left panel. */
  roomId: string;
  /** Wall-clock ms remaining in the wordPick phase (countdown). */
  msRemaining: number;
  /** wordPick total seconds (room setting), picks the timer's urgency stop. */
  phaseTotalSeconds?: number;
};

/**
 * The audience now sees the REAL game frame during wordPick, the same T6
 * 3-column shell (left players · center canvas · right chat) with an EMPTY
 * read-only canvas: rather than the old bare text screen. A "{drawer} is
 * picking…" banner fades in over the canvas (an OVERLAY, never a route swap,
 * so the canvas NODE stays mounted across the phase exit fade). The inert
 * canvas is empty because ws.ts clears `$finalizedStrokes` on wordPick entry
 * (the stale-flash fix).
 */
export function WaitingForDrawer({
  drawerName,
  drawerId,
  playerId,
  game,
  players,
  roomId,
  msRemaining,
  phaseTotalSeconds,
}: WaitingForDrawerProps) {
  const secondsLeft = useDeadlineCountdown(msRemaining);
  const scores = buildTurnScores(players, game.turnSummaries);
  return (
    <div
      class={`drawing-phase-shell waiting-for-drawer ${PAGE_TEAR_IN_CLASS}`}
      data-testid="drawing-phase-shell"
      data-phase="wordPick"
    >
      <PageTearLine />
      <aside class="drawing-phase-col drawing-phase-col--left" data-testid="drawing-phase-col-left">
        <PlayersPanel
          players={players}
          drawerId={drawerId}
          myId={playerId}
          scores={scores}
          roomId={roomId}
        />
      </aside>

      <div
        class="drawing-phase-col drawing-phase-col--center"
        data-testid="drawing-phase-col-center"
      >
        <div class="drawing-phase-header">
          <span class="drawing-phase-round" data-testid="drawing-phase-round">
            Turn {game.currentTurnIndex + 1}/{game.turnOrder.length}
          </span>
          {/* Q9: drawer can drop during wordPick too, same paused chip as the
              drawing phase (the server cancelled its pick timer). */}
          <DrawingPhaseTimer
            secondsLeft={secondsLeft}
            totalSeconds={phaseTotalSeconds}
            paused={!!game.drawerDisconnect}
          />
        </div>

        {/* The empty inert canvas: same node the drawing phase mounts, so the
            real frame reads (canvas + players + timer), not a text screen. The
            "is picking" banner is an OVERLAY inside the wrapper so the canvas
            node stays mounted while the banner fades. */}
        <div
          class="canvas-wrapper drawing-phase-canvas-wrapper wordpick-canvas-wrapper"
          data-testid="waiting-for-drawer"
          style={{ cursor: "default" }}
        >
          <DrawingCanvas readOnly />
          <div
            class="wordpick-picking-banner"
            data-testid="wordpick-picking-banner"
            role="status"
            aria-live="polite"
          >
            <span class="wordpick-picking-name">{drawerName}</span> is picking
            <span class="wordpick-picking-dots" aria-hidden="true">
              …
            </span>
          </div>
        </div>
      </div>

      <aside
        class="drawing-phase-col drawing-phase-col--right"
        data-testid="drawing-phase-col-right"
      >
        <GuessChatFeed chatLog={game.chatLog} />
      </aside>
    </div>
  );
}
